import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, Bell, User, LogOut, Settings, Eye, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserNotifications, useMarkNotificationAsRead, useDeleteNotification } from '../../hooks';
import { Badge } from '../common';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, logout, isAdmin } = useAuth();
  const { data: notifications } = useUserNotifications({ limit: 5 });
  const markAsReadMutation = useMarkNotificationAsRead();
  const deleteNotificationMutation = useDeleteNotification();
  
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const unreadCount = notifications?.users?.filter(n => !n.emailSent).length || 0;
  const notificationsList = notifications?.users || [];

  // Cerrar menús al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // const handleMarkAsRead = async (notificationId: string) => {
  //   try {
  //     await markAsReadMutation.mutateAsync(notificationId);
  //   } catch (error) {
  //     console.error('Error marcando como leída:', error);
  //   }
  // };

  // const handleDeleteNotification = async (notificationId: string) => {
  //   try {
  //     await deleteNotificationMutation.mutateAsync(notificationId);
  //   } catch (error) {
  //     console.error('Error eliminando notificación:', error);
  //   }
  // };

  const formatTimeAgo = (date: string | undefined) => {
    if (!date) return 'Fecha desconocida';
    
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInMinutes = Math.floor((now.getTime() - notificationDate.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Ahora';
    if (diffInMinutes < 60) return `Hace ${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `Hace ${Math.floor(diffInMinutes / 60)}h`;
    return `Hace ${Math.floor(diffInMinutes / 1440)}d`;
  };

  return (
    <header className="bg-white/90 backdrop-blur shadow-sm border-b border-slate-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Lado izquierdo */}
          <div className="flex items-center">
            {/* Botón de menú móvil */}
            <button
              type="button"
              className="lg:hidden -ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-lg text-brinsa-azulOscuro hover:text-brinsa-azul focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brinsa-celeste"
              onClick={onMenuClick}
            >
              <span className="sr-only">Abrir menú</span>
              <Menu className="h-6 w-6" />
            </button>
          </div>

          {/* Lado derecho */}
          <div className="flex items-center space-x-4">
            {/* Notificaciones */}
            <div className="relative" ref={notificationsRef}>
              <button
                type="button"
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="relative p-2 text-brinsa-azulOscuro hover:text-brinsa-azul focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brinsa-celeste rounded-lg transition-colors"
              >
                <span className="sr-only">Ver notificaciones</span>
                <Bell className="h-6 w-6" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full min-w-[1.25rem] h-5">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Dropdown de notificaciones */}
              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-brinsa-celeste/25 z-50 max-h-96 overflow-hidden">
                  <div className="p-4 border-b border-brinsa-celeste/20">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-brinsa-azul">Notificaciones</h3>
                      {unreadCount > 0 && (
                        <Badge variant="error" size="sm">
                          {unreadCount} nuevas
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="max-h-64 overflow-y-auto">
                    {notificationsList.length > 0 ? (
                      notificationsList.map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-4 border-b border-brinsa-celeste/10 hover:bg-brinsa-celeste/5 transition-colors ${
                            !notification.emailSent ? 'bg-blue-50/50' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <h4 className={`text-sm font-medium truncate ${
                                  !notification.emailSent ? 'text-brinsa-azul' : 'text-brinsa-azulOscuro'
                                }`}>
                                  {notification.title}
                                </h4>
                                {!notification.emailSent && (
                                  <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
                                )}
                              </div>
                              <p className="text-xs text-brinsa-azulOscuro/80 line-clamp-2 mb-2">
                                {notification.body}
                              </p>
                              <p className="text-xs text-brinsa-azulOscuro/60">
                                {formatTimeAgo(notification.sentAt)}
                              </p>
                            </div>
                          </div>

                          {/* Acciones rápidas */}
                          {/* <div className="flex items-center space-x-2 mt-2">
                            {!notification.emailSent && (
                              <button
                                onClick={() => handleMarkAsRead(notification.id)}
                                disabled={markAsReadMutation.isPending}
                                className="inline-flex items-center px-2 py-1 text-xs text-brinsa-azul hover:bg-brinsa-celeste/20 rounded transition-colors"
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                Leído
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteNotification(notification.id)}
                              disabled={deleteNotificationMutation.isPending}
                              className="inline-flex items-center px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Eliminar
                            </button>
                          </div> */}
                        </div>
                      ))
                    ) : (
                      <div className="p-6 text-center">
                        <Bell className="w-8 h-8 text-brinsa-celeste mx-auto mb-2" />
                        <p className="text-sm text-brinsa-azulOscuro/80">No tienes notificaciones</p>
                      </div>
                    )}
                  </div>

                  {/* Footer con enlace a ver todas */}
                  {notificationsList.length > 0 && (
                    <div className="p-3 border-t border-brinsa-celeste/20">
                      <Link
                        to="/notifications"
                        onClick={() => setNotificationsOpen(false)}
                        className="block text-center text-sm text-brinsa-azul hover:text-brinsa-azulOscuro font-medium transition-colors"
                      >
                        Ver todas las notificaciones
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Menú de usuario */}
            <div className="relative" ref={userMenuRef}>
              <button
                type="button"
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-brinsa-celeste/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brinsa-celeste transition-colors"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <div className="w-8 h-8 bg-brinsa-azul rounded-full flex items-center justify-center">
                  <span className="text-white font-medium text-sm">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-brinsa-azul">{user?.name}</p>
                  <p className="text-xs text-brinsa-azulOscuro/70">{user?.cedula}</p>
                </div>
              </button>

              {/* Dropdown del usuario */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg py-1 z-50 border border-brinsa-celeste/25">
                  <Link
                    to="/profile"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center px-4 py-2 text-sm text-brinsa-azulOscuro hover:bg-brinsa-celeste/10 transition-colors"
                  >
                    <User className="mr-3 h-4 w-4" />
                    Mi Perfil
                  </Link>
                  {/* <Link
                    to="/profile#settings"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center px-4 py-2 text-sm text-brinsa-azulOscuro hover:bg-brinsa-celeste/10 transition-colors"
                  >
                    <Settings className="mr-3 h-4 w-4" />
                    Configuración
                  </Link> */}
                  <hr className="my-1 border-brinsa-celeste/20" />
                  <button
                    onClick={logout}
                    className="flex items-center w-full px-4 py-2 text-sm text-brinsa-azulOscuro hover:bg-brinsa-celeste/10 transition-colors"
                  >
                    <LogOut className="mr-3 h-4 w-4" />
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;